import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/format";
import { StatusPill, LevelBadge, sponsorPill } from "@/components/Badges";
import SponsorForm from "@/components/SponsorForm";
import ContactSponsorsDialog from "@/components/ContactSponsorsDialog";
import { CpCell } from "@/pages/Dashboard";
import { useAuth } from "@/context/AuthContext";

export default function SponsorsPage({ bilans = false }) {
  const { session } = useAuth();
  const current = session.club.season;
  const [season, setSeason] = useState(current);
  const [seasons, setSeasons] = useState([current]);
  const [list, setList] = useState(null);
  const [form, setForm] = useState({ open: false, sponsor: null });
  const [contactOpen, setContactOpen] = useState(false);
  const archived = season !== current;
  const load = () => api.get("/sponsors", { params: { season } }).then((r) => setList(r.data));
  useEffect(() => { setSeason(current); api.get("/seasons").then((r) => setSeasons(r.data.seasons)); }, [current]);
  useEffect(() => { load(); }, [season]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (s) => {
    if (!window.confirm(`Supprimer « ${s.name} » et toutes ses données ?`)) return;
    try { await api.delete(`/sponsors/${s.id}`); toast.success("Sponsor supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid={bilans ? "bilans-page" : "sponsors-page"}>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{bilans ? "Bilans de visibilité" : "Sponsors"}</h1>
          <p className="text-sm text-[#5B665F] mt-2">{bilans ? "Générez un bilan imprimable à envoyer à chaque sponsor." : "Ajoutez, modifiez ou supprimez vos partenaires."}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
        {seasons.length > 1 && (
          <select className="field h-11 w-auto" value={season} onChange={(e) => setSeason(e.target.value)} data-testid="season-select">
            {seasons.map((x) => <option key={x} value={x}>Saison {x}{x === current ? " (en cours)" : " (archive)"}</option>)}
          </select>
        )}
        {!bilans && !archived && <button className="btn-outline" onClick={() => setContactOpen(true)} data-testid="sponsors-contact-button">Contacter tous mes sponsors</button>}
        {!bilans && !archived && <button className="btn-primary self-start sm:self-auto" onClick={() => setForm({ open: true, sponsor: null })} data-testid="add-sponsor-button">Ajouter un sponsor</button>}
        </div>
      </div>
      {archived && <p className="mt-4 text-sm bg-[#E3EBFA] text-[#1F4396] rounded-lg px-3 py-2" data-testid="archive-banner">Vous consultez l'archive de la saison {season}.</p>}
      <section className="card mt-8 overflow-hidden fade-up">
        {!list ? <p className="p-6 text-[#5B665F]">Chargement…</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="bg-[#F8F9F6] text-[#5B665F] text-[13px]">
                <tr>{["Sponsor", "Niveau", "Montant", "Encaissé", "Contreparties", "Fin de contrat", "Statut", ""].map((h, i) => <th key={i} className="text-left font-medium px-5 py-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {list.map((s) => (
                  <tr key={s.id} className="border-t border-[#E1E4DD] hover:bg-[#FAFBF8] transition-colors" data-testid={`sponsor-row-${s.id}`}>
                    <td className="px-5 py-4"><Link to={`/sponsors/${s.id}`} className="link font-medium" data-testid={`sponsor-link-${s.id}`}>{s.name}</Link><div className="text-xs text-[#5B665F] mt-0.5">{s.contact_name}</div></td>
                    <td className="px-5 py-4"><LevelBadge level={s.level} /></td>
                    <td className="px-5 py-4 whitespace-nowrap">{fmtMoney(s.amount)}</td>
                    <td className="px-5 py-4 whitespace-nowrap text-[#5B665F]">{fmtMoney(s.paid)}</td>
                    <td className="px-5 py-4"><CpCell s={s} /></td>
                    <td className="px-5 py-4 whitespace-nowrap">{fmtDate(s.end_date)}</td>
                    <td className="px-5 py-4"><StatusPill status={s.status}>{sponsorPill(s)}</StatusPill></td>
                    <td className="px-5 py-4">
                      {bilans || archived ? (
                        <Link to={`/bilans/${s.id}`} className="btn-primary btn-sm" data-testid={`bilan-generate-${s.id}`}><FileText size={15} /> Générer le bilan</Link>
                      ) : (
                        <div className="flex justify-end gap-1">
                          <button className="btn-ghost btn-sm" onClick={() => setForm({ open: true, sponsor: s })} title="Modifier" data-testid={`sponsor-edit-${s.id}`}><Pencil size={15} /></button>
                          <button className="btn-ghost btn-sm text-[#8A4B00]" onClick={() => remove(s)} title="Supprimer" data-testid={`sponsor-delete-${s.id}`}><Trash2 size={15} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {list.length === 0 && <tr><td colSpan={8} className="px-5 py-8 text-[#5B665F]" data-testid="sponsors-empty">Aucun sponsor pour l'instant.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <ContactSponsorsDialog open={contactOpen} onOpenChange={setContactOpen} />
      <SponsorForm open={form.open} sponsor={form.sponsor} onOpenChange={(o) => setForm({ ...form, open: o })} onSaved={load} />
    </div>
  );
}
